import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "../../config/axiosInstance";
import { CRM } from "../../config/apiConfig";
import TaskCardPickerModal from "../modals/TaskCardPickerModal";
import { useApplication } from "../../contexts/ApplicationContext";
// O TaskModal é enorme (arrasta Vonage, Twilio, TipTap, Quill…) e este componente
// fica montado em todo o layout autenticado: importá-lo estático colocava tudo
// isso no bundle inicial. Só é baixado quando alguém escolhe um card.
const TaskModal = lazy(() => import("../modals/TaskModal"));

// Eventos (window) que ligam este componente ao resto do app:
//  - OPEN_EVENT: abre a escolha de card (o botão "Criar nova tarefa" do funil
//    manda `detail: { funnel }` para listar primeiro os cards daquele funil).
//  - CHANGED_EVENT: avisa que o card mudou (uma tarefa foi criada); o quadro do
//    CRM, se estiver aberto, recarrega o card.
export const OPEN_TASK_PICKER_EVENT = "relinq:open-task-picker";
export const CARD_CHANGED_EVENT = "relinq:card-changed";

// Funil aberto na tela agora (o quadro do CRM informa): a tecla T já usa esse
// funil e pula direto para a escolha do card. Fora do quadro fica `null`.
let activeFunnel = null;
export const setTaskPickerActiveFunnel = (funnel) => {
  activeFunnel = funnel?.id ? funnel : null;
};

const isEditableTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const tag = String(target.tagName || "").toUpperCase();
  return (
    target.isContentEditable ||
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT"
  );
};

/**
 * "Criar tarefa" em qualquer tela: a tecla T (fora de campos de digitação)
 * ou o botão do funil abrem a escolha de funil e card (com um funil aberto na
 * tela, a escolha já começa pelos cards dele); ao escolher, abre só o modal de
 * nova tarefa daquele card (TaskModal em `taskOnlyMode`). Montado no layout
 * autenticado (`routes/Container.jsx`).
 */
export default function GlobalTaskCreator() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFunnel, setPickerFunnel] = useState(null);
  const [target, setTarget] = useState(null); // { task, history }
  const busyRef = useRef(false);
  // Criar tarefa exige `crm.task.create` (a mesma chave do servidor); a ref evita
  // reinstalar os ouvintes a cada atualização das permissões.
  const { can } = useApplication();
  const canCreateTaskRef = useRef(true);
  canCreateTaskRef.current = can("crm.task.create");

  useEffect(() => {
    busyRef.current = pickerOpen || Boolean(target);
  }, [pickerOpen, target]);

  const openPicker = useCallback((funnel = null) => {
    if (busyRef.current || !canCreateTaskRef.current) return;
    setPickerFunnel(funnel);
    setPickerOpen(true);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key?.toLowerCase() !== "t") return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return;
      }
      if (event.repeat || event.isComposing || event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;
      if (!canCreateTaskRef.current) return;
      // Sem preventDefault o "t" iria parar no campo de busca que abre em foco.
      event.preventDefault();
      openPicker(activeFunnel);
    };
    const onOpenEvent = (event) => openPicker(event?.detail?.funnel || null);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_TASK_PICKER_EVENT, onOpenEvent);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_TASK_PICKER_EVENT, onOpenEvent);
    };
  }, [openPicker]);

  const handlePick = async (cardId, chosenFunnel) => {
    setPickerOpen(false);
    // lembra o funil escolhido: a seta "voltar" reabre a lista de cards dele
    if (chosenFunnel?.id) setPickerFunnel(chosenFunnel);
    try {
      const [detailsRes, historyRes] = await Promise.all([
        axios.get(`${CRM.GET_CARD}/${cardId}/details`),
        axios.get(`${CRM.GET_CARD_HISTORY}/${cardId}/history`),
      ]);
      const data = detailsRes?.data?.data;
      if (!data?.id) return;
      setTarget({
        task: {
          ...data,
          checklist: Array.isArray(data.checklist) ? data.checklist : [],
          customFields: Array.isArray(data.customFields)
            ? data.customFields
            : [],
        },
        history: historyRes?.data?.data || [],
      });
    } catch (err) {
      console.error("Erro ao abrir o card para criar tarefa", err);
    }
  };

  return (
    <>
      <TaskCardPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        funnel={pickerFunnel}
        onPick={handlePick}
      />
      {target ? (
        <Suspense fallback={null}>
        <TaskModal
          taskOnlyMode
          task={target.task}
          history={target.history}
          setHistory={(updater) =>
            setTarget((prev) =>
              prev
                ? {
                    ...prev,
                    history:
                      typeof updater === "function"
                        ? updater(prev.history)
                        : updater,
                  }
                : prev,
            )
          }
          setSelectedTask={(updater) =>
            setTarget((prev) =>
              prev
                ? {
                    ...prev,
                    task:
                      typeof updater === "function"
                        ? updater(prev.task)
                        : updater,
                  }
                : prev,
            )
          }
          updateTask={() => {}}
          onRefetchCard={(cardId) =>
            window.dispatchEvent(
              new CustomEvent(CARD_CHANGED_EVENT, {
                detail: { cardId: cardId || target.task?.id },
              }),
            )
          }
          stages={[]}
          selectedFunnel={null}
          taskRatingConfig={{}}
          onClose={() => setTarget(null)}
          onTaskOnlyBack={() => {
            setTarget(null);
            setPickerOpen(true);
          }}
        />
        </Suspense>
      ) : null}
    </>
  );
}
